import {
  assertPublicationScope,
} from "../ActivePublicationResolver";


export class POSActivityDomainResolverError extends Error {
  constructor(
    message,
    {
      code =
        "POS_ACTIVITY_DOMAIN_RESOLVER_ERROR",
      operation = null,
      stage = null,
      domainId = null,
      details = {},
      cause = null,
    } = {}
  ) {
    super(message);

    this.name =
      "POSActivityDomainResolverError";

    this.code = code;
    this.operation = operation;
    this.stage = stage;
    this.domainId = domainId;

    this.details = Object.freeze({
      ...details,
    });

    if (cause !== null) {
      this.cause = cause;
    }
  }
}


/**
 * Generic POSActivity domain resolver.
 *
 * Responsibilities:
 *
 * - validate the activity scope;
 * - use the active Studio publication as the
 *   canonical source of domainId;
 * - resolve the registered POSDomainAdapter;
 * - validate activity compatibility;
 * - obtain the Domain Runtime Configuration
 *   through PublicationRuntimeBridge;
 * - validate that configuration through the
 *   POSDomainAdapter;
 * - return an immutable resolved domain object.
 *
 * Explicitly excluded:
 *
 * - activity_type -> domain fallback;
 * - Retail fallback;
 * - Restaurant fallback;
 * - session creation;
 * - component resolution;
 * - catalog execution;
 * - payment;
 * - inventory mutation;
 * - accounting;
 * - NF525;
 * - persistence.
 */
export function createPOSActivityDomainResolver({
  posDomainRegistry,
  publicationRuntimeBridge,
} = {}) {
  assertPOSDomainRegistry(
    posDomainRegistry
  );

  assertPublicationRuntimeBridge(
    publicationRuntimeBridge
  );


  async function resolvePOSActivityDomain(
    input = {}
  ) {
    const operation =
      "resolvePOSActivityDomain";

    const context =
      normalizeActivityContext(
        input,
        operation
      );

    const {
      companyUuid,
      activityUuid,
      activityType,
      permissions,
      features,
      subscription,
      activePublication,
    } = context;


    if (
      activePublication === null
      || activePublication === undefined
    ) {
      throw new POSActivityDomainResolverError(
        "No active Studio publication is available for the activity.",
        {
          code: "NO_ACTIVE_PUBLICATION",
          operation,
          stage: "publication",
          details: {
            companyUuid,
            activityUuid,
            activityType,
          },
        }
      );
    }


    const domainId =
      normalizeRequiredString(
        activePublication.domainId,
        "activePublication.domainId",
        {
          operation,
          stage: "publication",
          code: "INVALID_ACTIVE_PUBLICATION",
        }
      );


    try {
      assertPublicationScope(
        activePublication,
        {
          companyUuid,
          activityUuid,
          domainId,
        }
      );
    } catch (error) {
      throw new POSActivityDomainResolverError(
        "Active Studio publication does not match the requested activity scope.",
        {
          code:
            "PUBLICATION_SCOPE_MISMATCH",
          operation,
          stage: "publicationScope",
          domainId,
          details: {
            companyUuid,
            activityUuid,
            activityType,
          },
          cause: error,
        }
      );
    }


    let adapter;

    try {
      adapter =
        posDomainRegistry
          .resolvePOSDomain(
            domainId
          );
    } catch (error) {
      throw new POSActivityDomainResolverError(
        `No POS domain adapter is registered for domainId "${domainId}".`,
        {
          code: "DOMAIN_NOT_REGISTERED",
          operation,
          stage: "resolveDomain",
          domainId,
          details: {
            companyUuid,
            activityUuid,
            activityType,
          },
          cause: error,
        }
      );
    }


    const activityContext =
      Object.freeze({
        companyUuid,
        activityUuid,
        activityType,
        permissions,
        features,
        subscription,
        activePublication,
      });


    let supported;

    try {
      supported =
        await adapter.supportsActivity(
          activityContext
        );
    } catch (error) {
      throw new POSActivityDomainResolverError(
        `POS domain "${domainId}" failed while checking activity compatibility.`,
        {
          code:
            "ACTIVITY_SUPPORT_CHECK_FAILED",
          operation,
          stage: "supportsActivity",
          domainId,
          details: {
            companyUuid,
            activityUuid,
            activityType,
          },
          cause: error,
        }
      );
    }


    if (supported !== true) {
      throw new POSActivityDomainResolverError(
        `POS domain "${domainId}" does not support the requested activity.`,
        {
          code: "UNSUPPORTED_ACTIVITY",
          operation,
          stage: "supportsActivity",
          domainId,
          details: {
            companyUuid,
            activityUuid,
            activityType,
          },
        }
      );
    }


    let runtimeConfiguration;

    try {
      runtimeConfiguration =
        await publicationRuntimeBridge
          .resolveRuntimeConfigurationByPublicationId(
            activePublication.publicationId,
            {
              companyUuid,
              activityUuid,
              domainId,
            }
          );
    } catch (error) {
      throw new POSActivityDomainResolverError(
        `Active Studio publication is incompatible with POS domain "${domainId}".`,
        {
          code:
            "PUBLICATION_INCOMPATIBLE",
          operation,
          stage:
            "resolveRuntimeConfiguration",
          domainId,
          details: {
            publicationId:
              activePublication
                .publicationId,
          },
          cause: error,
        }
      );
    }


    if (
      runtimeConfiguration === null
      || runtimeConfiguration === undefined
    ) {
      throw new POSActivityDomainResolverError(
        "Publication Runtime configuration is unavailable.",
        {
          code:
            "INVALID_RUNTIME_CONFIGURATION",
          operation,
          stage:
            "resolveRuntimeConfiguration",
          domainId,
          details: {
            publicationId:
              activePublication
                .publicationId,
          },
        }
      );
    }


    let validationResult;

    try {
      validationResult =
        await adapter
          .validateConfiguration(
            runtimeConfiguration
          );
    } catch (error) {
      throw new POSActivityDomainResolverError(
        `POS domain "${domainId}" failed while validating Runtime configuration.`,
        {
          code:
            "DOMAIN_CONFIGURATION_VALIDATION_FAILED",
          operation,
          stage:
            "validateConfiguration",
          domainId,
          cause: error,
        }
      );
    }


    if (
      validationResult === false
      || (
        isPlainObject(
          validationResult
        )
        && validationResult.valid === false
      )
    ) {
      throw new POSActivityDomainResolverError(
        `Runtime configuration is invalid for POS domain "${domainId}".`,
        {
          code:
            "INVALID_DOMAIN_CONFIGURATION",
          operation,
          stage:
            "validateConfiguration",
          domainId,
          details: {
            issues:
              isPlainObject(
                validationResult
              )
                && Array.isArray(
                  validationResult.issues
                )
                ? Object.freeze([
                    ...validationResult
                      .issues,
                  ])
                : Object.freeze([]),
          },
        }
      );
    }


    return Object.freeze({
      companyUuid,
      activityUuid,
      activityType,
      domainId,

      publication:
        activePublication,

      runtimeConfiguration,

      adapter,

      permissions,
      features,
      subscription,
    });
  }


  return Object.freeze({
    resolvePOSActivityDomain,
  });
}


function assertPOSDomainRegistry(
  registry
) {
  if (
    registry === null
    || typeof registry !== "object"
    || typeof registry.resolvePOSDomain
      !== "function"
  ) {
    throw new POSActivityDomainResolverError(
      "posDomainRegistry must expose resolvePOSDomain().",
      {
        code:
          "INVALID_POS_DOMAIN_REGISTRY",
        stage: "initialization",
      }
    );
  }
}


function assertPublicationRuntimeBridge(
  bridge
) {
  if (
    bridge === null
    || typeof bridge !== "object"
    || typeof bridge
      .resolveRuntimeConfigurationByPublicationId
      !== "function"
  ) {
    throw new POSActivityDomainResolverError(
      "publicationRuntimeBridge must expose resolveRuntimeConfigurationByPublicationId().",
      {
        code:
          "INVALID_PUBLICATION_RUNTIME_BRIDGE",
        stage: "initialization",
      }
    );
  }
}


function normalizeActivityContext(
  input,
  operation
) {
  if (!isPlainObject(input)) {
    throw new POSActivityDomainResolverError(
      "POSActivity resolution input must be a plain object.",
      {
        code:
          "INVALID_ACTIVITY_CONTEXT",
        operation,
        stage: "activityContext",
      }
    );
  }

  const companyUuid =
    normalizeRequiredString(
      input.companyUuid
        ?? input.company_uuid,
      "companyUuid",
      {
        operation,
        stage: "activityContext",
        code:
          "INVALID_ACTIVITY_CONTEXT",
      }
    );

  const activityUuid =
    normalizeRequiredString(
      input.activityUuid
        ?? input.activity_uuid,
      "activityUuid",
      {
        operation,
        stage: "activityContext",
        code:
          "INVALID_ACTIVITY_CONTEXT",
      }
    );

  const activityType =
    normalizeRequiredString(
      input.activityType
        ?? input.activity_type,
      "activityType",
      {
        operation,
        stage: "activityContext",
        code:
          "INVALID_ACTIVITY_CONTEXT",
      }
    );

  const permissions =
    freezeArray(
      input.permissions ?? []
    );

  const features =
    freezeArray(
      input.features ?? []
    );

  const subscription =
    freezeShallowValue(
      input.subscription ?? null
    );

  return Object.freeze({
    companyUuid,
    activityUuid,
    activityType,
    permissions,
    features,
    subscription,
    activePublication:
      input.activePublication
      ?? input.active_publication
      ?? null,
  });
}


function normalizeRequiredString(
  value,
  fieldName,
  {
    operation = null,
    stage = null,
    code =
      "INVALID_ACTIVITY_CONTEXT",
  } = {}
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new POSActivityDomainResolverError(
      `${fieldName} must be a non-empty string.`,
      {
        code,
        operation,
        stage,
        details: {
          fieldName,
        },
      }
    );
  }

  return value.trim();
}


function freezeArray(value) {
  if (!Array.isArray(value)) {
    throw new POSActivityDomainResolverError(
      "permissions and features must be arrays when provided.",
      {
        code:
          "INVALID_ACTIVITY_CONTEXT",
        stage: "activityContext",
      }
    );
  }

  return Object.freeze([
    ...value,
  ]);
}


function freezeShallowValue(value) {
  if (Array.isArray(value)) {
    return Object.freeze([
      ...value,
    ]);
  }

  if (isPlainObject(value)) {
    return Object.freeze({
      ...value,
    });
  }

  return value;
}


function isPlainObject(value) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
  ) {
    return false;
  }

  const prototype =
    Object.getPrototypeOf(value);

  return (
    prototype === Object.prototype
    || prototype === null
  );
}
